'use client'

import { useState, useRef, useEffect } from 'react'
import { Search, X, Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSearchHistory } from '../hooks/useSearchHistory'

interface SearchInputWithHistoryProps {
  value: string
  onChange: (value: string) => void
  onSearch?: (value: string) => void
  placeholder?: string
  className?: string
  inputClassName?: string
  variant?: 'hero' | 'header' | 'sub-header'
  storageKey?: string
  skipValidation?: boolean
}

export function SearchInputWithHistory({
  value,
  onChange,
  onSearch,
  placeholder = 'Tìm kiếm...',
  className,
  inputClassName,
  variant = 'header',
  storageKey = 'search-history',
  skipValidation = false,
}: SearchInputWithHistoryProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [error, setError] = useState<string>('')
  const { history, addToHistory, removeFromHistory } = useSearchHistory(storageKey)
  
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const filteredHistory = value.trim()
    ? history.filter((item) =>
      item.toLowerCase().includes(value.toLowerCase())
    )
    : history

  // Xử lý Click ra ngoài khối search (bao gồm cả input và dropdown)
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    
    // Dùng mousedown để bắt sớm nhất, ngăn các sự kiện click khác can thiệp
    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [])

  const handleSelect = (selectedValue: string) => {
    if (!skipValidation && selectedValue.trim().length < 4) {
      setError('Từ khóa tìm kiếm phải có ít nhất 4 ký tự')
      return
    }

    setError('')
    onChange(selectedValue)
    addToHistory(selectedValue)
    onSearch?.(selectedValue)
    setIsDropdownOpen(false)
  }

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault()
    const trimmedValue = value.trim()

    // Skip validation if skipValidation is true
    if (skipValidation) {
      setError('')
      if (trimmedValue) {
        addToHistory(trimmedValue)
      }
      onSearch?.(trimmedValue)
      setIsDropdownOpen(false)
      return
    }

    if (!trimmedValue) {
      setError('Vui lòng nhập từ khóa tìm kiếm')
      return
    }

    if (trimmedValue.length < 4) {
      setError('Từ khóa tìm kiếm phải có ít nhất 4 ký tự')
      return
    }

    setError('')
    addToHistory(trimmedValue)
    onSearch?.(trimmedValue)
    setIsDropdownOpen(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setIsDropdownOpen(false)
      handleSubmit()
    } else if (e.key === 'Escape') {
      setIsDropdownOpen(false)
      inputRef.current?.blur()
    }
  }

  const handleRemoveHistory = (
    e: React.MouseEvent,
    term: string
  ) => {
    e.stopPropagation()
    removeFromHistory(term)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    onChange(val)
    setError('')
    setIsDropdownOpen(true)
  }

  const handleInputInteraction = () => {
    setIsDropdownOpen(true)
  }

  const isHero = variant === 'hero'
  const isSubHeader = variant === 'sub-header'

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      {isHero ? (
        <div className="bg-white p-2 rounded-lg">
          <div className="relative">
            <input
              ref={inputRef}
              type="search"
              name="hs_search_history_input"
              autoComplete="off"
              spellCheck="false"
              autoCorrect="off"
              autoCapitalize="off"
              value={value}
              onChange={handleInputChange}
              onFocus={handleInputInteraction}
              onClick={handleInputInteraction}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className={cn(
                'w-full bg-[#F9FAFC] px-6 py-3 rounded-lg text-sm leading-[22px] text-[#717171] outline-none pr-16',
                inputClassName
              )}
            />
            <button
              type="button"
              onClick={() => handleSubmit()}
              className="cursor-pointer absolute right-0 top-0 h-12 w-12 bg-[#FFECEC] hover:bg-[#FFECEC]/90 rounded-md flex items-center justify-center"
            >
              <Search className="w-5 h-5 text-[#B91C1C]" strokeWidth={1.5} />
            </button>
          </div>
          {error && (
            <p className="text-red-600 text-sm mt-2 px-2">{error}</p>
          )}
        </div>
      ) : isSubHeader ? (
        <>
          <input
            ref={inputRef}
            type="search"
            name="hs_search_history_input"
            autoComplete="off"
            spellCheck="false"
            autoCorrect="off"
            autoCapitalize="off"
            value={value}
            onChange={handleInputChange}
            onFocus={handleInputInteraction}
            onClick={handleInputInteraction}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={cn(
              'h-10 w-full rounded-full border border-[#D4D4D4] bg-white px-5 pr-10 text-sm placeholder:text-[#717171] outline-none focus:border-[#1E40AF] transition-all',
              inputClassName
            )}
          />
          <button
            type="button"
            onClick={() => handleSubmit()}
            className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer"
            aria-label="Search"
          >
            <Search className="h-5 w-5 text-gray-500 hover:text-[#1E40AF]" />
          </button>
          {error && (
            <p className="text-red-600 text-[11px] mt-1 absolute top-full right-4">{error}</p>
          )}
        </>
      ) : (
        <>
          <input
            ref={inputRef}
            type="search"
            name="hs_search_history_input"
            autoComplete="off"
            spellCheck="false"
            autoCorrect="off"
            autoCapitalize="off"
            value={value}
            onChange={handleInputChange}
            onFocus={handleInputInteraction}
            onClick={handleInputInteraction}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={cn(
              'h-10 w-full rounded-lg border-0 bg-[#f5f6f8] px-4 pr-10 text-sm placeholder:text-[#717171] outline-none',
              inputClassName
            )}
          />
          <button
            type="button"
            onClick={() => handleSubmit()}
            className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
            aria-label="Search"
          >
            <Search className="h-5 w-5 text-gray-500 hover:text-gray-700" />
          </button>
          {error && (
            <p className="text-red-600 text-sm mt-1 absolute top-full left-0">{error}</p>
          )}
        </>
      )}

      {/* Custom Dropdown thay thế cho Radix Popover/Command */}
      {isDropdownOpen && filteredHistory.length > 0 && (
        <div 
          className="absolute top-[calc(100%+8px)] left-0 z-50 w-full min-w-[300px] bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden flex flex-col"
        >
          <div className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b border-gray-100">
            Tìm kiếm gần đây
          </div>
          <ul className="max-h-[300px] overflow-y-auto py-1 m-0 list-none flex-1">
            {filteredHistory.map((term) => (
              <li
                key={term}
                onClick={() => handleSelect(term)}
                className="flex justify-between items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer transition-colors"
                role="option"
                aria-selected="false"
              >
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400 shrink-0" />
                  <span className="truncate">{term}</span>
                </div>
                <button
                  type="button"
                  onClick={(e) => handleRemoveHistory(e, term)}
                  className="p-1 hover:bg-gray-200 rounded shrink-0"
                  aria-label="Xoá lịch sử"
                >
                  <X className="w-3 h-3 text-gray-400" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
